<script>
    // The prompt: the working log of the action under way, and whatever the game
    // is waiting on (choices, a cost, cards to pick, OK). Shared by the desktop
    // bottom bar (STGame) and the phone layout, which passes `phone` to lay it
    // out as sheets rather than floating panels.
    import Card from './Card.svelte';
    import Log from './Log.svelte';
    import LogItem from './LogItem.svelte';
    import { draggable } from 'svelte-drag';
    import { blur } from 'svelte/transition';
    import { tick } from 'svelte/internal';
    import { state, ui, res, prefs, live } from './stores.js';
    import { undo, replayPrev, replayNext, getCard } from './game.js';
    import { advance, convert, isTouchDevice, activePlayer, currentPlayer, hasLogText } from './ui.js';
    import { getHints, autoActionButton } from './parse.js';
    import { isMe } from './net.js';
    import { costAdjustments, canAdjustCost, adjustCost, renderCost } from './costs.js';

    export let phone = false;

    let actionList;
    let hintsList;

    $: if(actionList && $state && Array.isArray($state.workingLog) && $ui.waiting.length > 0){
        tick().then(() => {
            if(actionList) actionList.scrollTo({top: actionList.scrollHeight, behaviour: 'smooth'});
        });
    }

    $: if(actionList && $res.replaying && $state.log){
        tick().then(() => {
            if(actionList) actionList.scrollTo({top: actionList.scrollHeight, behaviour: 'smooth'});
        });
    }

    $: if(hintsList){
        hintsList.scrollLeft = hintsList.scrollWidth;
    }

    // 'Don't ask again for this card' for wildcard prompts. Reset whenever the
    // prompt changes so a tick never carries over to the next question.
    let rememberThis = false;
    let lastRememberKey = false;
    $: {
        let key = $ui.waiting && $ui.waiting[0] ? $ui.waiting[0].rememberKey : false;
        if(key != lastRememberKey){
            lastRememberKey = key;
            rememberThis = false;
        }
    }
    function rememberChoice(key, choice){
        if(!key || !rememberThis) return;
        $prefs.wildcardAuto = {...($prefs.wildcardAuto || {}), [key]: choice};
        rememberThis = false;
    }
</script>

{#if $res.replaying}
<div use:draggable={{disabled: phone}} bind:this={actionList}  class="working_log waiting_display" class:phone style="justify-content: space-evenly; {$ui.choosingMarket ? 'margin-bottom: 30vh;' : 'margin-top: 20vh;'}">
    <Log showTitle={false} log={$state.log} onlyLatest={true}/>
</div>
{:else if $state.workingLog && $state.workingLog.length > 0}
    <div use:draggable={{disabled: phone}} bind:this={actionList}  class="working_log waiting_display" class:phone style="justify-content: space-evenly; {$ui.choosingMarket ? 'margin-bottom: 30vh;' : 'margin-top: 20vh;'}">
            <div class="action_list">
                {#each $state.workingLog.filter(l => hasLogText(l)) as l, i}
                    <div style="white-space: break-spaces;" transition:blur|global="{{ duration: 200 }}">
                        <LogItem log={l} />.
                    </div>
                {/each}
                {#if $ui.actionSummary && $ui.actionSummary.length > 0}
                    <div style="font-style: italic;">→ {$ui.actionSummary.join(', ')}</div>
                {/if}
            </div>
            {#if $ui.waiting[0] && $ui.waiting[0].cards}
            <!--<div use:draggable={{disabled: phone}} class="choices" class:phone style="z-index: 350; bottom: 8.1vmin; padding-top: 1vmin; top: unset; flex-wrap: nowrap;">-->
                <div style="display: flex; align-items: center; gap: 10px;">
                    <LogItem log={$ui.waiting[0].message} />
                    {#if $ui.waiting[0].liveInfo && $ui.waiting[0].liveInfo($ui)}
                        {@html $live.infoToDisplay || ''}
                    {/if}
                    {#if $ui.waiting[0].extraChoices}
                        {#each $ui.waiting[0].extraChoices as c}
                            {#if c.value}
                                <button disabled={c.condition && !c.condition($ui, $state)} on:click={e => { advance(c.value) }}>{@html convert(c.name)}</button>
                            {:else if c.action}
                                <button disabled={c.condition && !c.condition($ui, $state)} on:click={e => { c.action() }}>{@html convert(c.name)}</button>
                            {/if}
                        {/each}
                    {/if}
                    <button class:ok={!($ui.waiting[0].optional && !$ui.selection || $ui.waiting[0].cards.length == 0)} disabled={$ui.waiting[0].condition && !$ui.waiting[0].condition($ui, $state)} on:click={e => {if($ui.waiting && $ui.waiting[0] && $ui.waiting[0].cards && $ui.waiting[0].hasOwnProperty('number')) advance($ui.multiSelection); else advance($ui.selection);}}>
                        {#if $ui.waiting[0].optional && !$ui.selection || $ui.waiting[0].cards.length == 0}
                            {#if $ui.waiting[0].noCard}{$ui.waiting[0].noCard}{:else}No card{/if}
                        {:else}
                            {#if $ui.waiting[0].ok}
                                {$ui.waiting[0].ok}
                            {:else}
                                OK
                            {/if}
                        {/if}
                    </button>
                </div>
            {/if}
    </div>
{/if}
<div class="textgroup waiting_display" class:phone>
    {#if $res.replaying}
        <button on:click={replayPrev}><b>&larr;</b></button>
        Replaying: {Math.round(($res.nextDelta + 1)/$res.deltas.length * 100)}% through game
        <button on:click={replayNext}><b>&rarr;</b></button>
    {:else if !isMe($state.activePlayer) && !$state.ended && $ui && !$prefs.showAllPrompts} <!-- $ui to make reactive to waiting? -->
        Captain {$state.players[$state.activePlayer].captain} is taking actions...
       <!-- <div bind:this={currentLog} showTitle={false} class="current_log">
            <Log showTitle={false} log={logSoFar} />
        </div>-->
    {:else}
        {#if $ui.waiting.length > 0}
            {#if $ui.waiting[0].cancellable}
                <button class="cancel" on:click={e => {advance('cancelled')}}>{$ui.waiting[0].cancel || 'Cancel'}</button>
            {:else if isMe(currentPlayer($state)) && isMe(activePlayer($state)) && $prefs.canUndo && $res.prevStates.length > $res.undoThreshold && $state.phase == 'action' && !$ui.waiting.some(w => !w.turnMarker)}
                <button on:click={undo}>
                    Undo
                </button>
            {/if}
            {#if $prefs.showHints && getHints($ui.selection).length > 0 && $state.phase == 'action' && (!$ui.waiting[0] || !$ui.waiting[0].cards)}
                    <div bind:this={hintsList} style="display: flex; align-items: center; gap: 10px; white-space: nowrap; width: 100%;">
                        <button on:touchstart={e => { if(isTouchDevice()) autoActionButton($ui.selection)} } on:click={e => { if(!isTouchDevice()) autoActionButton($ui.selection)} }>
                            <b>
                                {#if getHints($ui.selection)[0].name.includes('play')}
                                    Play
                                {:else}
                                    {getHints($ui.selection)[0].name}
                                {/if}
                            </b>
                            &nbsp;<i>{$ui.selection.name}</i>
                        </button>
                        →
                        {#each getHints($ui.selection) as h, i}
                            <div style="font-style: italic;"><b>{h.name.replace(/play/i, 'Action')}:&nbsp;</b>{@html convert(h.list)}</div>
                            <!--<button on:click={e => { doAction($state.players[$ui.seat], h.action, {card: $ui.selection.uniqueID, chosenHint: [h] }) }}>{h.name}</button>0-->
                            {#if i < getHints($ui.selection).length - 1}
                            &nbsp;OR&nbsp;
                            {/if}
                        {/each}
                        
                    </div>
            
            {:else if $ui.waiting[0]}
                {#if $ui.waiting[0].choices}
                    <div use:draggable={{disabled: phone}} class="choices" class:phone style="z-index: 350; " >
                        {#if $ui.waiting[0].showLog}<LogItem log={$state.log[$state.log.length - 1]} />{/if}
                        {#if $ui.waiting[0].card}
                            <div style="display: flex; overflow-y: auto;">
                                {#each $ui.waiting[0].card as c, i}<!--bind:this={$ui.waiting[0].cardElement} -->
                                    <Card bind:this={$ui.waitingComponent[i]} single partOfLook c={typeof c == 'number' ? getCard(c) : c} />
                                {/each}
                            </div>
                        {/if}
                        <LogItem log={$ui.waiting[0].message} />
                        <div style="display: flex; flex-wrap: wrap; justify-content: space-evenly;">
                            {#each $ui.waiting[0].choices as c}
                                {#if c == '#nl'}
                                    <div style="flex-basis: 100%; height: 0;"></div>
                                {:else}
                                    <button disabled={c.startsWith('!') && !$prefs.allowAllActions} style="margin: 0.5vmin;" on:click={e => {rememberChoice($ui.waiting[0].rememberKey, c); advance(c); e.stopPropagation();}}>{@html convert(c.startsWith('!') ? c.slice(1) : c, true)}</button>
                                {/if}
                            {/each}
                        </div>
                        {#if $ui.waiting[0].rememberKey}
                            <label style="display: block; text-align: center; font-size: 0.8em;">
                                <input type="checkbox" bind:checked={rememberThis} /> Don't ask again for this card
                            </label>
                        {/if}
                    </div>
                {:else if $ui.waiting[0].cost}
                    <div use:draggable={{disabled: phone}} class="choices" class:phone style="z-index: 350; ">
                        <LogItem log={$ui.waiting[0].message} />
                        <div>
                            Cost {@html convert(renderCost($ui.waiting[0].cost))}
                        </div>
                        <div style="display: flex; justify-content: space-evenly;">
                            {#if $ui.waiting[0].cancellable}
                                <button class="cancel" on:click={e => advance('cancelled')}>Cancel</button>
                            {/if}
                            <!-- One control per substitution in force: which pile pays for which requirement -->
                            {#each costAdjustments($ui.waiting[0].cost) as pair}
                                <div>
                                    {@html convert(`[[${pair.to}]] costs:`)}
                                    <button on:click={e => { adjustCost($ui.waiting[0].cost, {...pair, dir: '-'}); $ui = $ui;}} disabled={!canAdjustCost($ui.waiting[0].cost, {...pair, dir: '-'})}>
                                        {@html convert(`- [[${pair.from}]]`)}
                                    </button>
                                    <button on:click={e => { adjustCost($ui.waiting[0].cost, {...pair, dir: '+'}); $ui = $ui;}} disabled={!canAdjustCost($ui.waiting[0].cost, {...pair, dir: '+'})}>
                                        {@html convert(`+ [[${pair.from}]]`)}
                                    </button> 
                                </div>
                            {/each}
                            <button on:click={e => {advance($ui.waiting[0].cost)}}>Pay</button>
                        </div>
                    </div>
                {:else if $ui.waiting[0].cards && (!$state.workingLog || $state.workingLog.length == 0)}
                    <!--<div use:draggable={{disabled: phone}} class="choices" class:phone style="z-index: 350; bottom: 8.1vmin; padding-top: 1vmin; top: unset; flex-wrap: nowrap;">-->
                    <div style="display: flex; align-items: center; gap: 10px;">
                        <LogItem log={$ui.waiting[0].message} />
                        {#if $ui.waiting[0].liveInfo && $ui.waiting[0].liveInfo($ui)}
                            {@html $live.infoToDisplay || ''}
                        {/if}
                        {#if $ui.waiting[0].extraChoices}
                            {#each $ui.waiting[0].extraChoices as c}
                                <button disabled={c.condition && !c.condition($ui, $state)} on:click={e => { advance(c.value) }}>{@html convert(c.name)}</button>
                            {/each}
                        {/if}
                        {#if $ui.waiting[0].textChoice}
                            <input type="text" bind:value={$ui.waiting[0].textChoice.input} on:input={$ui.waiting[0].cards = $ui.waiting[0].textChoice.func($ui.waiting[0].textChoice.input)} />
                        {/if}
                        <button class:ok={$ui.waiting[0].noCard == 'OK' || (!$ui.waiting[0].optional || $ui.selection) && $ui.waiting[0].cards.length != 0} disabled={$ui.waiting[0].condition && !$ui.waiting[0].condition($ui, $state)} on:click={e => {if($ui.waiting && $ui.waiting[0] && $ui.waiting[0].cards && $ui.waiting[0].hasOwnProperty('number')) advance($ui.multiSelection); else advance($ui.selection);}}>
                            {#if $ui.waiting[0].optional && !$ui.selection || $ui.waiting[0].cards.length == 0}
                                {#if $ui.waiting[0].noCard}{$ui.waiting[0].noCard}{:else}No card{/if}
                            {:else}
                                {#if $ui.waiting[0].ok}
                                    {$ui.waiting[0].ok}
                                {:else}
                                    OK
                                {/if}
                            {/if}
                        </button>
                    </div>
                {:else if !$ui.waiting[0].cards && $state.workingLog && $state.workingLog.length > 0  && (!$state.botWorking || $ui.waiting[0].forceView)}
                    <!--<div use:draggable={{disabled: phone}} class="choices" class:phone style="z-index: 350; bottom: 8.1vmin; padding-top: 1vmin; top: unset; flex-wrap: nowrap;">-->
                    <div style="display: flex; align-items: center; gap: 10px;">
                            <LogItem log={$ui.waiting[0].message} />
                            {#if $ui.waiting[0].liveInfo && $ui.waiting[0].liveInfo($ui)}
                                {@html $live.infoToDisplay || ''}
                            {/if}
                            <button class="ok" on:click={e => {advance();}}>
                                {#if $ui.waiting[0].ok}
                                    {$ui.waiting[0].ok}
                                {:else}
                                    OK
                                {/if}
                            </button>
                        </div>
                        
                {:else if !$ui.waiting[0].cards  && (!$state.botWorking || $ui.waiting[0].forceView)}
                    {#if $ui.waiting[0].message}<div style="flex-grow: 1;">{@html convert($ui.waiting[0].message, true)}</div>{/if}
                    <button class="ok" disabled={$ui.waiting[0].condition && !$ui.waiting[0].condition($ui, $state)} on:click={e => {if($ui.waiting && $ui.waiting[0] && $ui.waiting[0].cards && $ui.waiting[0].cards.hasOwnProperty('number')) advance($ui.multipleSelection); else advance($ui.selection);}}>
                        {#if $ui.waiting[0].ok}
                            {$ui.waiting[0].ok}
                        {:else}
                            OK
                        {/if}
                    </button> 
                {/if}
            {/if}
        {/if}
    {/if}
</div>

<style>
    .ok {
        background-color: green;
    }

    .textgroup {
        display: flex;
        align-items: center;
        flex-wrap: nowrap;
        white-space: nowrap;
        margin-right: 10px;
    }

    .waiting_display {
        overflow-y: auto; 
        height: 100%; 
        width: 100%; 
        flex-wrap: nowrap; 
        margin-left: 2vmin; 
        margin-right: 2vmin; 
        justify-content: space-between;
        gap: 10px;
    }

    button {
		border-radius: 1vmin; 
        color: white;
		background: rgba(150,150,150,0.5);
		filter: drop-shadow(0.5vmin 0.5vmin 0.5vmin slategray);
	}

    button:disabled { 
        color: gray;
        background: rgba(0,0,0,0.5);
    }

    .choices {
        position: fixed;
        z-index: 300;
		left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        margin: auto;
		padding: 3vmin;
		padding-bottom: 1vmin;
        height: fit-content;
        width: fit-content;
		max-height: 50vh;
		max-width: 90vw;
		min-width: 60vw;
		overflow: auto;
		background-color: darkblue;
		box-shadow: 0 0 5px 5px white; 
        color: white;
        display: block;
        white-space: normal;
        border-radius: 3vmin;
    }

    .working_log {
        position: fixed;
        z-index: 300;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        margin: auto;
        padding: 2vmin;
        height: fit-content;
        width: fit-content;
        max-height: 20vh;
        max-width: 90vw;
        min-width: 60vw;
        overflow: auto;
        backdrop-filter: blur(5px);
        -webkit-backdrop-filter: blur(5px);
        color: white;
        background-color: rgba(2,2,2,0.5);
        border: unset;
        border-radius: 30px;
        box-shadow: 0 0 10px 5px white;
        color: white;
        display: block;
    }

    .action_list {
        flex-grow: 1; 
        margin-left: 1vmin; 
        margin-right: 1vmin;
        white-space: nowrap;
        overflow-x: scroll;
        scrollbar-width: none;
        display: flex;
        align-items: center;
        gap: 10px;
        flex-wrap: wrap;
    }
    
    .action_list::-webkit-scrollbar{
        display: none;
    }

    /* Phone (docs/phone-plan.md): the waiting line sits in the dock, the working
       log is a banner under the status bar, and choices are a sheet above the dock. */
    .waiting_display.phone {
        margin: 0;
        height: auto;
        overflow: visible;
        white-space: normal;
        flex-wrap: wrap;
        font-size: 14px;
        gap: 6px;
    }
    .working_log.phone, .choices.phone {
        left: calc(8px + env(safe-area-inset-left, 0px));
        right: calc(8px + env(safe-area-inset-right, 0px));
        width: auto;
        min-width: 0;
        max-width: none;
        margin: 0;
        padding: 10px 12px;
        font-size: 14px;
        box-shadow: 0 6px 24px rgba(0,0,0,.6);
    }
    .working_log.phone {
        top: calc(env(safe-area-inset-top, 0px) + var(--phone-top, 72px));
        bottom: auto;
        max-height: 26vh;
        border-radius: 12px;
    }
    .choices.phone {
        top: auto;
        bottom: calc(env(safe-area-inset-bottom, 0px) + var(--phone-dock, 96px));
        max-height: 46vh;
        border-radius: 14px;
        background-color: #0f1a2c;
        border: 1px solid #2d4266;
    }
    .phone button {
        font-size: 14px;
        min-height: 38px;
        padding: 6px 12px;
        border-radius: 10px;
        filter: none;
        touch-action: manipulation;
    }
    .choices.phone button {
        flex: 1 1 40%;
    }
</style>
